"use client"
import React, { useState, useEffect } from "react"
import AssistantService from "../../services/assistant.service";
import { 
  ArrowLeft, 
  Save, 
  Upload,
  User,
  Settings,
  Brain,
  Database,
  Loader2
} from "lucide-react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select"
import AvatarUploader from "../AvatarUploader/AvatarUploader"
import useToast from "../../hooks/useToast"
import { useAuth } from "../../useAuth"
import { useOrganizations } from "../../hooks/useOrganizations"

export default function AssistantConfigureView({ 
  assistant, 
  onBack, 
  onSave,
  onUpdate,
  llmModels = [],
  templates: contextTemplates = [] // This will be called "avatars" in the UI but kept as "templates" for API compatibility
}) {
  const { success, error } = useToast();
  const { user } = useAuth();
  const { currentAccount } = useOrganizations();
  
  // Estados del formulario
  const [saving, setSaving] = useState(false);
  const [avatarBase64, setAvatarBase64] = useState(""); // Imagen en base64
  const [jsonSchemaError, setJsonSchemaError] = useState(""); // Error de validación de JSON
  // Local state for templates (avatars)
  const [templates, setTemplates] = useState(contextTemplates || []);
  // Local state for available skills from API
  const [availableSkills, setAvailableSkills] = useState([]);

  // Fetch templates and skills from backend on mount (for always up-to-date dropdown)
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const result = await AssistantService.listTemplates();
        if (result.success && Array.isArray(result.data)) {
          setTemplates(result.data);
        } else {
          setTemplates(contextTemplates || []);
        }
      } catch (e) {
        setTemplates(contextTemplates || []);
      }
    };
    
    const fetchSkills = async () => {
      try {
        const result = await AssistantService.getSkills();
        if (result.success && result.data?.skills && Array.isArray(result.data.skills)) {
          setAvailableSkills(result.data.skills);
        } else {
          setAvailableSkills([]);
        }
      } catch (e) {
        setAvailableSkills([]);
      }
    };
    
    fetchTemplates();
    fetchSkills();
  }, []);

  const [formData, setFormData] = useState({
    // Información básica
    name: '',
    description: '',
    avatar: '', // URL para mostrar avatar existente
    type: 'general',
    
    // Configuración del modelo
    modelId: '',
    avatarTemplate: '', // Selected avatar ID
    temperature: 0.7,
    maxTokens: 2048,
    language: 'es',
    responseFormat: 'text',
    
    // Output Mode Configuration
    outputMode: 'normal', // normal, structured_json, stream
    jsonSchema: '',
    strictValidation: true,
    prettyPrint: false,
    indent: 2,
    
    // API fields
    memory: '',
    executionMode: 'reactive',
    skills: [], // Skills del assistant
    
    // Settings avanzados
    memoryEnabled: true,
    isActive: true,
    isDraft: false,
    
    // Avatar/Template
    templateId: ''
  });

  // Cargar datos del asistente al montar
  useEffect(() => {
    if (assistant) {
      
      const skillsRoot = Array.isArray(assistant.skills) ? assistant.skills : [];
      const skillsConfig = Array.isArray(assistant.config?.skills) ? assistant.config.skills : [];
      const allSkills = skillsRoot.length > 0 ? skillsRoot : skillsConfig;
      setFormData({
        name: assistant.name || '',
        description: assistant.description || '',
        avatar: assistant.avatar || '',
        type: assistant.type || 'general',
        modelId: assistant.config?.llmId || '',
        avatarTemplate: assistant.config?.assistantTemplate?.id || assistant.config?.templateId || '',
        temperature: assistant.config?.temperature || 0.7,
        maxTokens: assistant.config?.maxTokens || 2048,
        language: assistant.config?.language || 'es',
        responseFormat: assistant.config?.responseFormat || 'text',
        outputMode: assistant.config?.outputFormat?.mode || 'normal',
        jsonSchema: assistant.config?.outputFormat?.json_schema || '',
        strictValidation: assistant.config?.outputFormat?.strict_validation ?? true,
        prettyPrint: assistant.config?.outputFormat?.pretty_print ?? false,
        indent: assistant.config?.outputFormat?.indent || 2,
        memory: assistant.memory || '',
        executionMode: assistant.executionMode || 'reactive',
        skills: allSkills,
        memoryEnabled: assistant.config?.memoryEnabled ?? true,
        isActive: assistant.config?.isActive ?? true,
        isDraft: assistant.isDraft ?? false,
        templateId: assistant.config?.assistantTemplate?.id || assistant.config?.templateId || ''
      });
    }
  }, [assistant]);

  const handleSave = async () => {
    // Validaciones
    if (!formData.name.trim()) {
      error('Required field', 'Please enter a name for the assistant');
      return;
    }

    // Validar JSON Schema si está en modo structured_json o stream
    if ((formData.outputMode === 'structured_json' || formData.outputMode === 'stream') && formData.jsonSchema.trim()) {
      try {
        JSON.parse(formData.jsonSchema);
        setJsonSchemaError('');
      } catch (e) {
        error('Invalid JSON Schema', 'The JSON Schema is not valid. Please check the syntax.');
        setJsonSchemaError('Invalid JSON syntax');
        return;
      }
    }

    setSaving(true);
    try {
      const assistantData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        type: 'general', // Default type
        skills: formData.skills || [],
        reactAgentId: null,
        memory: formData.memory || "",
        executionMode: formData.executionMode || "reactive",
        config: {
          ...assistant?.config,
          llmId: formData.modelId,
          templateId: formData.avatarTemplate, // Usar templateId para el backend
          temperature: formData.temperature,
          maxTokens: formData.maxTokens,
          language: formData.language,
          responseFormat: formData.responseFormat,
          memoryEnabled: formData.memoryEnabled,
          isActive: formData.isActive,
          isDraft: formData.isDraft,
          // Output Format Configuration
          outputFormat: formData.outputMode === 'normal' ? null : {
            mode: formData.outputMode,
            ...(formData.outputMode === 'structured_json' || formData.outputMode === 'stream' ? {
              json_schema: formData.jsonSchema || null,
              strict_validation: formData.strictValidation,
              pretty_print: formData.prettyPrint,
              indent: formData.indent
            } : {})
          }
        },
        projectId: assistant?.projectId || null,
        avatar: avatarBase64 || formData.avatar || null
      };
      // Skills como array real
      const skills = Array.isArray(assistantData.skills) ? assistantData.skills : [];
      // Payload final
      const dataToSend = {
        ...assistantData,
        skills
      };
      if (assistant && assistant.id) {
        if (!assistant.id.trim()) {
          error('Invalid Assistant', 'Assistant ID is missing or invalid');
          return;
        }
        await onUpdate(assistant.id, dataToSend);
      } else {
        if (!user?.user_id && !user?.id) {
          error('Authentication Error', 'User information is missing');
          return;
        }
        if (!currentAccount?.id) {
          error('Account Error', 'Account information is missing');
          return;
        }
        await onSave(dataToSend);
      }
      success('Saved', assistant?.id ? 'Assistant updated successfully' : 'Assistant created successfully');
    } catch (err) {
      console.error('Save error:', err);
      error('Error', 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAvatarChange = (base64) => {
    setAvatarBase64(base64);
    updateFormData('avatar', base64);
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 backdrop-blur-sm border-b border-white/10 p-4 z-10 bg-black/80">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              onClick={onBack}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-purple-400" />
              <div>
                <span className="text-lg font-light text-white">
                  Configure Assistant
                </span>
                {assistant?.id && (
                  <span className="ml-2 text-xs text-gray-400">
                    (ID: {assistant.id.slice(0, 8)}...)
                  </span>
                )}
                {!assistant?.id && (
                  <span className="ml-2 text-xs text-green-400">
                    (New Assistant)
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {assistant?.id && (
              <Button
                onClick={onBack}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
        </div>
      </div>

      {/* Contenido del formulario - Layout de 2 Grids */}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          
          {/* Grid Izquierdo: Basic Information */}
          <div className="space-y-8">
            
            {/* Sección 1: Basic Information */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-center space-x-2 mb-6">
                <User className="w-5 h-5 text-blue-400" />
                <h3 className="text-white font-semibold text-lg">Basic Information</h3>
              </div>
              
              <div className="space-y-6">
                {/* Avatar */}
                <div>
                  <label className="text-gray-300 text-sm font-medium mb-3 block">Assistant Avatar</label>
                  <div className="space-y-4">
                    {/* Avatar and URL container */}
                    <div className="flex items-start space-x-4">
                      {/* Avatar preview with watermark button */}
                      <div className="relative w-32 h-32 rounded-xl border border-white/20 bg-white/10 overflow-hidden group">
                        {(avatarBase64 || (formData.avatar && !formData.avatar.includes('00000000-0000-0000-0000-000000000000'))) ? (
                          <img
                            src={avatarBase64 || formData.avatar}
                            alt="Avatar"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-center">
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <span className="text-gray-400 text-xs">No Avatar</span>
                          </div>
                        )}
                        
                        {/* Watermark button - appears on hover */}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <Button
                            onClick={() => document.getElementById('avatar-upload').click()}
                            size="sm"
                            variant="ghost"
                            className="bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            Change
                          </Button>
                        </div>
                      </div>
                      
                      {/* URL input field - shorter */}
                      <div className="flex-1 max-w-xs">
                        <label className="text-gray-300 text-xs font-medium mb-1 block">Paste image URL:</label>
                        <Input
                          value=""
                          onChange={e => updateFormData('avatar', e.target.value)}
                          placeholder="https://example.com/avatar.png"
                          className="w-full bg-black/60 border border-white/20 text-white placeholder-gray-500"
                        />
                      </div>
                    </div>
                    
                    {/* Hidden AvatarUploader */}
                    <div className="hidden">
                      <AvatarUploader onAvatarChange={handleAvatarChange} />
                    </div>
                    
                    {/* Hidden file input */}
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const base64 = event.target.result;
                            setAvatarBase64(base64);
                            updateFormData('avatar', base64);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Main Information */}
                <div className="space-y-4">
                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block">
                      Assistant Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                      placeholder="FastShop Order Confirmation (Copy)"
                      className="w-full bg-black/60 border border-white/20 text-white placeholder-gray-500"
                    />
                  </div>
                  
                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block">
                      Description
                    </label>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={(e) => updateFormData('description', e.target.value)}
                      placeholder="Order confirmation assistant for FastShop"
                      className="w-full bg-black/60 border border-white/20 text-white placeholder-gray-500 rounded-lg px-3 py-2 resize-none"
                    />
                  </div>

                  {/* Skills Section */}
                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block">
                      Skills
                    </label>
                    <Select 
                      value={formData.skills && formData.skills.length > 0 ? formData.skills[0] : "none"} 
                      onValueChange={(value) => updateFormData('skills', value === "none" ? [] : [value])}
                    >
                      <SelectTrigger className="w-full bg-black/60 border border-white/20 text-white">
                        <SelectValue placeholder="No skill selected">
                          {(() => {
                            const currentSkill = formData.skills && formData.skills.length > 0 ? formData.skills[0] : "none";
                            if (currentSkill === "none") return "No skill";
                            const skill = availableSkills?.find(s => s.name === currentSkill);
                            return skill ? skill.friendly_name : currentSkill;
                          })()}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border border-white/20 max-h-64 overflow-y-auto">
                        <SelectItem 
                          value="none"
                          className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                        >
                          No skill
                        </SelectItem>
                        {Array.isArray(availableSkills) && availableSkills.length > 0 && availableSkills.map((skill) => (
                          <SelectItem 
                            key={skill.name} 
                            value={skill.name}
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            {skill.friendly_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-gray-500 text-xs mt-1">
                      Choose a skill for your assistant (optional)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sección 3: Advanced Settings */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-center space-x-2 mb-6">
                <Settings className="w-5 h-5 text-orange-400" />
                <h3 className="text-white font-semibold text-lg">Advanced Settings</h3>
              </div>
              
              <div className="space-y-4">
                {/* Memory */}
                <div className="flex items-center justify-between p-4 bg-black/30 rounded-lg border border-white/10">
                  <div>
                    <label className="text-white font-medium text-sm">Memory</label>
                    <p className="text-gray-400 text-xs">Remember conversation context</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.memoryEnabled}
                      onChange={(e) => updateFormData('memoryEnabled', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer-checked:bg-purple-600 peer-focus:ring-2 peer-focus:ring-purple-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                  </label>
                </div>

                {/* Draft Mode */}
                <div className="flex items-center justify-between p-4 bg-black/30 rounded-lg border border-white/10">
                  <div>
                    <label className="text-white font-medium text-sm">Draft Mode</label>
                    <p className="text-gray-400 text-xs">Mark assistant as draft</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.isDraft}
                      onChange={(e) => updateFormData('isDraft', e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-700 rounded-full peer-checked:bg-orange-600 peer-focus:ring-2 peer-focus:ring-orange-500 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Grid Derecho: Información Técnica y Configuración del Modelo */}
          <div className="space-y-8">
            
            {/* Información Técnica (solo lectura) - Movida arriba */}
            {assistant && (
              <div className="bg-black/20 rounded-xl p-6 border border-white/5">
                <h3 className="text-gray-300 font-medium text-sm mb-4">Technical Information (Read only)</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Assistant ID</span>
                    <span className="text-gray-300 font-mono">{assistant.id?.substring(0, 8)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Project ID</span>
                    <span className="text-gray-300 font-mono">{assistant.projectId?.substring(0, 8) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Owner ID</span>
                    <span className="text-gray-300 font-mono">{assistant.ownerId?.substring(0, 8) || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Created</span>
                    <span className="text-gray-300">{assistant.createdAt ? new Date(assistant.createdAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Sección 2: AI Model Configuration - Movida abajo */}
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-center space-x-2 mb-6">
                <Brain className="w-5 h-5 text-green-400" />
                <h3 className="text-white font-semibold text-lg">AI Model Configuration</h3>
              </div>
              
              <div className="space-y-6">
                {/* LLM Model Selection */}
                <div>
                  <label className="text-gray-300 text-sm font-medium mb-2 block">
                    LLM Model
                  </label>
                  <Select value={formData.modelId} onValueChange={(value) => updateFormData('modelId', value)}>
                    <SelectTrigger className="w-full bg-black/60 border border-white/20 text-white">
                      <SelectValue placeholder="Select model">
                        {(() => {
                          const selectedLLM = llmModels.find(m => m.id === formData.modelId);
                          if (selectedLLM) {
                            return `${selectedLLM.name} (${selectedLLM.provider})`;
                          }
                          return "Select a model";
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border border-white/40 max-h-64 overflow-y-auto">
                      {Array.isArray(llmModels) && llmModels.length > 0 ? (
                        llmModels.map((model) => (
                          <SelectItem 
                            key={model.id} 
                            value={model.id}
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            <div className="flex justify-between items-center w-full">
                              <span className="font-medium">{model.name}</span>
                              <span className="text-xs text-gray-400">({model.provider})</span>
                            </div>
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem 
                          value="gpt-4o-mini"
                          className="text-white hover:bg-white/10 focus:bg-white/10 py-2"
                        >
                          <div className="flex justify-between items-center w-full">
                            <span className="font-medium">GPT-4o Mini</span>
                            <span className="text-xs text-gray-400">(OpenAI)</span>
                          </div>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">
                    GPT-4o Mini is recommended for development, GPT-4o for production
                  </p>
                </div>

                {/* Avatar Selection */}
                <div>
                  <label className="text-gray-300 text-sm font-medium mb-2 block">
                    Avatar
                  </label>
                  <Select value={formData.avatarTemplate} onValueChange={(value) => updateFormData('avatarTemplate', value)}>
                    <SelectTrigger className="w-full bg-black/60 border border-white/20 text-white">
                      <SelectValue placeholder="Select avatar">
                        {(() => {
                          
                          // Si es el UUID nulo, mostrar "No Avatar"
                          if (formData.avatarTemplate === "00000000-0000-0000-0000-000000000000") {
                            return "No Avatar";
                          }
                          
                          // Si hay un avatarTemplate seleccionado, buscar su nombre en la lista de templates
                          if (formData.avatarTemplate) {
                            const selectedTemplate = templates.find(t => t.id === formData.avatarTemplate);
                            if (selectedTemplate) {
                              return selectedTemplate.name;
                            }
                            // Si no se encuentra en la lista, mostrar el ID truncado
                            return `Avatar (${formData.avatarTemplate.substring(0, 8)}...)`;
                          }
                          // Si no hay avatarTemplate pero estamos editando un assistant, mostrar que no tiene avatar asignado
                          if (assistant) {
                            return "No avatar assigned";
                          }
                          // Para nuevos assistants, mostrar el placeholder por defecto
                          return "Select an avatar";
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border border-white/40 max-h-64 overflow-y-auto">
                      <SelectItem 
                        value="00000000-0000-0000-0000-000000000000"
                        className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                      >
                        No Avatar
                      </SelectItem>
                      {Array.isArray(templates) && templates.length > 0 ? (
                        templates.map((template) => (
                          <SelectItem 
                            key={template.id} 
                            value={template.id}
                            className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer py-2"
                          >
                            {template.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem 
                          value="general"
                          className="text-white hover:bg-white/10 focus:bg-white/10 py-2"
                        >
                          General Assistant
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-400 mt-1">
                    Choose an avatar that matches your assistant's role
                  </p>
                </div>

                {/* Language and Response Format */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block">
                      Primary Language
                    </label>
                    <Select value={formData.language} onValueChange={(value) => updateFormData('language', value)}>
                      <SelectTrigger className="w-full bg-black/60 border border-white/20 text-white">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border border-white/40">
                        <SelectItem value="es" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Spanish</SelectItem>
                        <SelectItem value="en" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">English</SelectItem>
                        <SelectItem value="fr" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">French</SelectItem>
                        <SelectItem value="pt" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Portuguese</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block">
                      Output Mode
                    </label>
                    <Select value={formData.outputMode} onValueChange={(value) => updateFormData('outputMode', value)}>
                      <SelectTrigger className="w-full bg-black/60 border border-white/20 text-white">
                        <SelectValue placeholder="Select output mode" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border border-white/40">
                        <SelectItem value="normal" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Normal</SelectItem>
                        <SelectItem value="structured_json" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Structured JSON</SelectItem>
                        <SelectItem value="stream" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Stream</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Conditional fields for structured_json and stream */}
                  {(formData.outputMode === 'structured_json' || formData.outputMode === 'stream') && (
                    <div className="col-span-2 grid grid-cols-2 gap-4 p-5 bg-purple-900/10 border border-purple-500/30 rounded-lg mb-4">
                      <div>
                        <label className="text-white text-sm font-medium mb-2 block">
                          JSON Schema
                        </label>
                        <textarea
                          value={formData.jsonSchema}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateFormData('jsonSchema', value);
                            // Validar JSON en tiempo real
                            if (value.trim()) {
                              try {
                                JSON.parse(value);
                                setJsonSchemaError('');
                              } catch (error) {
                                setJsonSchemaError('Invalid JSON syntax');
                              }
                            } else {
                              setJsonSchemaError('');
                            }
                          }}
                          placeholder='{"type": "object", "properties": {...}}'
                          className={`w-full bg-black/60 border text-white rounded-lg p-3 h-[280px] resize-none font-mono text-sm placeholder-gray-500 focus:ring-1 transition-colors ${
                            jsonSchemaError 
                              ? 'border-red-500 focus:border-red-400 focus:ring-red-400/50' 
                              : 'border-white/20 focus:border-purple-400 focus:ring-purple-400/50'
                          }`}
                        />
                        {jsonSchemaError ? (
                          <p className="text-xs text-red-400 mt-1.5">{jsonSchemaError}</p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-1.5">Enter a valid JSON schema for structured output</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="text-white text-sm font-medium mb-2 block">Output Options</label>
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-white/10">
                            <label htmlFor="strictValidation" className="text-sm text-gray-300 cursor-pointer flex-1">
                              Strict Validation
                            </label>
                            <input
                              type="checkbox"
                              id="strictValidation"
                              checked={formData.strictValidation}
                              onChange={(e) => updateFormData('strictValidation', e.target.checked)}
                              className="w-4 h-4 rounded cursor-pointer accent-purple-600"
                            />
                          </div>
                          
                          <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-white/10">
                            <label htmlFor="prettyPrint" className="text-sm text-gray-300 cursor-pointer flex-1">
                              Pretty Print
                            </label>
                            <input
                              type="checkbox"
                              id="prettyPrint"
                              checked={formData.prettyPrint}
                              onChange={(e) => updateFormData('prettyPrint', e.target.checked)}
                              className="w-4 h-4 rounded cursor-pointer accent-purple-600"
                            />
                          </div>
                          
                          <div className="p-3 bg-black/20 rounded-lg border border-white/10">
                            <label className="text-sm text-gray-300 mb-2 block">Indent Spaces</label>
                            <input
                              type="number"
                              min="1"
                              max="8"
                              value={formData.indent}
                              onChange={(e) => updateFormData('indent', parseInt(e.target.value) || 2)}
                              className="w-20 bg-black/60 border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:border-purple-400 focus:ring-1 focus:ring-purple-400/50 transition-colors"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-gray-300 text-sm font-medium mb-2 block">
                      Response Format
                    </label>
                    <Select value={formData.responseFormat} onValueChange={(value) => updateFormData('responseFormat', value)}>
                      <SelectTrigger className="w-full bg-black/60 border border-white/20 text-white">
                        <SelectValue placeholder="Select format" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border border-white/40">
                        <SelectItem value="text" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Text</SelectItem>
                        <SelectItem value="markdown" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">Markdown</SelectItem>
                        <SelectItem value="json" className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer">JSON</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Temperature */}
                <div>
                  <label className="text-gray-300 text-sm font-medium mb-3 block">
                    Temperature: {formData.temperature}
                  </label>
                  <div className="space-y-3">
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={formData.temperature}
                      onChange={(e) => updateFormData('temperature', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Precise (0)</span>
                      <span>Balanced (1)</span>
                      <span>Creative (2)</span>
                    </div>
                  </div>
                </div>
                
                {/* Max Tokens */}
                <div>
                  <label className="text-gray-300 text-sm font-medium mb-3 block">
                    Max Tokens: {formData.maxTokens}
                  </label>
                  <div className="space-y-3">
                    <input
                      type="range"
                      min="256"
                      max="4096"
                      step="256"
                      value={formData.maxTokens}
                      onChange={(e) => updateFormData('maxTokens', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Short (256)</span>
                      <span>Medium (2048)</span>
                      <span>Long (4096)</span>
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}